// Sign-in through the project's own IdP.
//
// A client portal that signs its people in at their own IdP (Keycloak, Entra
// ID…) frames us the way the office does, but hands over the user's ID token
// (SET_ID_TOKEN) instead of a token it minted itself. We trade it at our own
// backend (/auth/v1/oidc/exchange) for the same access token the office would
// have fetched, and ask the portal for a fresh ID token a minute before that
// one runs out (TOKEN_EXPIRED) — which is also when the user's IdP groups are
// looked at again.
//
// Unlike SET_TOKEN, an ID token is only taken from a framer we were told about
// (VITE_EMBED_ORIGINS): it signs us in as somebody, and a hostile framer must
// not be able to choose who.
//
// Same contract as zehn-task / zehn-desk (src/shared/utils/idTokenExchange.js).
// The exchange deliberately uses fetch, not the shared axios client: a refused
// ID token (401) must not run that client's 401 handling (clear storage, ask
// the parent for a new token), which would turn one bad token into a loop.

import { env } from './env.js';

const embedOrigins = () =>
  (env('VITE_EMBED_ORIGINS') || '')
    .split(',')
    .map((origin) => origin.trim())
    .filter(Boolean);

export function isTrustedIdTokenSender(event) {
  return event.source === window.parent && embedOrigins().includes(event.origin);
}

export class IdTokenExchangeError extends Error {
  constructor(status, code, message) {
    super(message || code);
    this.name = 'IdTokenExchangeError';
    this.status = status;
    this.code = code;
  }
}

// A loop rather than /\/+$/, which static analysis flags as a backtracking risk.
function withoutTrailingSlashes(url) {
  let end = url.length;
  while (end > 0 && url[end - 1] === '/') end -= 1;
  return url.slice(0, end);
}

// Resolves to {access_token, expires_in, granted_scopes, user, tenant}; rejects
// with the backend's {detail: {code, message}} as an IdTokenExchangeError.
export async function exchangeIdToken(apiDomain, { tenant, idToken }) {
  const response = await fetch(`${withoutTrailingSlashes(apiDomain)}/auth/v1/oidc/exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenant, id_token: idToken }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = body?.detail || {};
    throw new IdTokenExchangeError(
      response.status,
      detail.code || 'exchange_failed',
      detail.message
    );
  }
  return body;
}

// Ask the framer for a fresh ID token a minute before the access token expires.
export function scheduleRenewal(expiresInSeconds, origin) {
  const delay = Math.max((expiresInSeconds - 60) * 1000, 10_000);
  return setTimeout(() => window.parent.postMessage({ type: 'TOKEN_EXPIRED' }, origin), delay);
}
